import React, { useEffect, useMemo, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { PatientSelfDataContext } from '../../../../../context/PatientSelfDataContext';
import { db, auth } from '../../../../../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';

// Hooks (v2)
import useAutosave from '../hooks/useAutosave';
import useAccessibility from '../hooks/useAccessibility';
import { HomeIcon, ChevronLeftIcon, ChevronRightIcon, RefreshIcon, CheckIcon } from '@heroicons/react/solid';

// Utils (v2)
import normalizeAnswer from '../utils/normalizeAnswer';
import formatDisplayAnswer from '../utils/formatDisplayAnswer';
import { resizeTextarea, createTextareaRefCallback } from '../../../../../utils/textareaResize';
import { otherOptionFieldKey } from '../../utils/sanitizeOptionForKey';
import validateAnswer from '../utils/validateAnswer';

// Optional: reuse existing components where helpful
import DateOfBirthPicker from '../../components/DateOfBirthPicker';
import './FormTemplate.css';

/*
  FormTemplate (v2)
  - One-question-at-a-time experience with a separate review step
  - Optimistic UI + debounced autosave using existing Firestore schema (no backend refactor)
  - Accessibility: focus management, aria-live announcements, reduced-motion awareness
  - This component intentionally lives alongside v1 so teams can A/B test UX and performance

  Design notes for maintainers
  - We keep the data source exactly the same as v1: read the current user's form snapshot
    from PatientSelfDataContext, and write answers back to Firestore under the same paths.
  - We avoid large architectural changes here; the purpose is to validate the UX layer
    and prove out debounced autosave + resilience. A later phase will shift authority
    and storage design to the backend.
  - We only save the current question's answers when the user interacts (debounced) or
    navigates next/previous. We mark the question as seen and optionally as skipped.
  - We compute local progress to render the progress bar; we do not change server progress
    semantics in v2 quick wins.
*/

export default function FormTemplate({ formTitle }) {
  const navigate = useNavigate();
  const { patientDataModel } = useContext(PatientSelfDataContext);
  const { register, setValue, watch, reset, handleSubmit } = useForm();

  // Screen reader announcements + deterministic focus when questions change
  const liveRegionRef = useRef(null);
  const focusAnchorRef = useRef(null);
  const { prefersReducedMotion, focusQuestionHeading, announce } = useAccessibility({
    liveRegionRef,
    focusAnchorRef
  });

  // Resolve current form data from context by title (matches v1 behavior)
  const allFormData = useMemo(() =>
    Object.entries(patientDataModel?.formData || {}).map(([key, form]) => ({ key, ...form })),
  [patientDataModel]);

  const currentFormData = useMemo(() =>
    allFormData.find(form => form.formTitle === formTitle),
  [allFormData, formTitle]);

  // Extract panels/questions structure
  const panels = useMemo(() => currentFormData?.formSubsections || {}, [currentFormData]);
  const panelKeys = useMemo(() => (
    Object.keys(panels || {})
      .map(k => parseInt(k, 10))
      .filter(n => !Number.isNaN(n))
      .sort((a, b) => a - b)
  ), [panels]);

  const [state, setState] = useState({
    panelIndex: panelKeys[0] || 1,
    questionIndex: 1,
    mode: 'questions', // 'questions' | 'review'
    saving: false,
    lastError: null
  });

  // Compute current question
  const currentPanel = useMemo(() => panels?.[state.panelIndex] || null, [panels, state.panelIndex]);
  const currentQuestion = useMemo(() => currentPanel?.questions?.[state.questionIndex] || null, [currentPanel, state.questionIndex]);

  // Compute counts for progress bar
  const totalQuestions = useMemo(() => (
    Object.values(panels || {}).reduce((acc, p) => acc + Object.keys(p.questions || {}).length, 0)
  ), [panels]);

  const submittedAnswers = useMemo(() => {
    // Derive answers from Firestore snapshot for review
    const entries = [];
    const form = currentFormData;
    if (!form?.formSubsections) return entries;
    Object.entries(form.formSubsections).forEach(([pIdx, panel]) => {
      Object.entries(panel.questions || {}).forEach(([qIdx, q]) => {
        if (q?.subquestions) {
          const sub = Object.values(q.subquestions).map(sq => ({
            question: sq.subquestionDisplayText,
            answer: sq.answer ?? 'No answer given'
          }));
          entries.push({
            questionTitle: q.questionTitle,
            questionVarName: q.questionVarName,
            answers: sub,
            wasSkipped: q.wasSkipped === true
          });
        }
      });
    });
    return entries;
  }, [currentFormData]);

  const answeredCount = useMemo(() => submittedAnswers.filter(e => (
    e.wasSkipped || e.answers.some(a => a.answer !== null && a.answer !== undefined && a.answer !== 'No answer given')
  )).length, [submittedAnswers]);

  // Prefill the current question into RHF for editing
  useEffect(() => {
    reset();
    if (!currentQuestion?.subquestions) return;
    Object.entries(currentQuestion.subquestions).forEach(([subIdx, subq]) => {
      const key = `question_${state.panelIndex}_${state.questionIndex}_${subIdx}`;
      // Use existing saved answer if present
      const normalized = normalizeAnswer(subq.inputType, subq.answer);
      if (normalized !== undefined) setValue(key, normalized);
    });
    // Screen-reader announcement + focus
    if (currentQuestion) {
      announce(`Question: ${currentQuestion.questionTitle}`);
      focusQuestionHeading();
    }
  }, [state.panelIndex, state.questionIndex, currentQuestion, reset, setValue, announce, focusQuestionHeading]);

  // Debounced autosave hooks (separate for text vs select to allow longer quiescence for toggles)
  const autosaveInputs = useAutosave({ delayMs: 400 });
  const autosaveSelects = useAutosave({ delayMs: 700 });

  // Write queue/coalescer refs
  const writeRevisionRef = useRef(0);
  const clientWriteSeqRef = useRef(0);
  const inFlightRef = useRef(null);
  const dirtyRef = useRef(false);

  const saveQuestionNow = React.useCallback(async (formValues, { isSkipping = false } = {}, writeRevision, clientSeq) => {
    const user = auth.currentUser;
    if (!user || !currentFormData?.key || !currentQuestion?.subquestions) return;

    setState(prev => ({ ...prev, saving: true, lastError: null }));

    try {
      const processed = Object.entries(currentQuestion.subquestions).map(([subIdx, subq]) => {
        const key = `question_${state.panelIndex}_${state.questionIndex}_${subIdx}`;
        const raw = formValues[key];
        const valid = isSkipping ? null : validateAnswer(subq, raw);
        const normalized = isSkipping ? null : normalizeAnswer(subq.inputType, valid);
        return {
          subIdx,
          subq,
          finalAnswer: normalized,
          displayAnswer: isSkipping ? (currentQuestion.skipButtonText || "I'm unable to provide this information") : formatDisplayAnswer(subq, normalized)
        };
      });

      // Firestore transaction mirrors v1 schema for the current question
      await runTransaction(db, async (transaction) => {
        const patientRef = doc(db, 'patients', user.uid);
        const snap = await transaction.get(patientRef);
        if (!snap.exists()) return;

        const formKey = currentFormData.key;
        const updates = {};
        const data = snap.data() || {};
        const existingSeq = data?.formData?.[formKey]?.formSubsections?.[state.panelIndex]?.questions?.[state.questionIndex]?.clientSeq || 0;
        if (clientSeq !== undefined && existingSeq >= clientSeq) {
          // Stale write; ignore
          return;
        }

        processed.forEach(({ subIdx, subq, finalAnswer }) => {
          updates[`formData.${formKey}.formSubsections.${state.panelIndex}.questions.${state.questionIndex}.subquestions.${subIdx}`] = {
            ...subq,
            answer: finalAnswer
          };
        });

        updates[`formData.${formKey}.formSubsections.${state.panelIndex}.questions.${state.questionIndex}.hasSeenQuestion`] = true;
        updates[`formData.${formKey}.formSubsections.${state.panelIndex}.questions.${state.questionIndex}.wasSkipped`] = !!isSkipping;
        updates[`formData.${formKey}.lastUpdated`] = serverTimestamp();
        if (clientSeq !== undefined) {
          updates[`formData.${formKey}.formSubsections.${state.panelIndex}.questions.${state.questionIndex}.clientSeq`] = clientSeq;
        }

        transaction.update(patientRef, updates);
      });

    } catch (err) {
      setState(prev => ({ ...prev, lastError: err?.message || 'Failed to save' }));
      throw err;
    } finally {
      // Coalescer will clear saving after final flush
    }
  }, [currentFormData?.key, currentQuestion?.subquestions, currentQuestion?.skipButtonText, state.panelIndex, state.questionIndex]);

  // Enqueue save with single in-flight write and stale-response protection
  const enqueueSave = React.useCallback((formValues, options = {}) => {
    const currentRevision = ++writeRevisionRef.current;
    const clientSeq = ++clientWriteSeqRef.current;

    const run = async () => {
      try {
        await saveQuestionNow(formValues, options, currentRevision, clientSeq);
      } catch (_) {
        // Error handled in saveQuestionNow
      } finally {
        if (currentRevision !== writeRevisionRef.current) return; // newer write took over
        if (dirtyRef.current) {
          dirtyRef.current = false;
          const latest = watch();
          return enqueueSave(latest, options);
        }
        setState(prev => ({ ...prev, saving: false }));
      }
    };

    if (inFlightRef.current) {
      dirtyRef.current = true;
      return inFlightRef.current;
    }
    inFlightRef.current = run().finally(() => { inFlightRef.current = null; });
    return inFlightRef.current;
  }, [saveQuestionNow, watch]);

  // Determine if current question contains select inputs (longer quiescence)
  const hasSelectInputs = useMemo(() => !!currentQuestion?.subquestions && Object.values(currentQuestion.subquestions).some(sq => sq.inputType === 'selectOne' || sq.inputType === 'selectMultiple'), [currentQuestion]);

  // Wire debounced autosave to form value changes on the current question
  useEffect(() => {
    if (!currentQuestion?.subquestions) return;
    const subscription = watch((vals) => {
      // Only autosave for current question keys to avoid noise
      const payload = {};
      let hasAny = false;
      Object.keys(currentQuestion.subquestions).forEach((subIdx) => {
        const key = `question_${state.panelIndex}_${state.questionIndex}_${subIdx}`;
        if (key in vals) {
          payload[key] = vals[key];
          hasAny = true;
        }
      });
      if (hasAny) {
        const autosaver = hasSelectInputs ? autosaveSelects : autosaveInputs;
        autosaver.schedule(async () => {
          enqueueSave(vals);
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, currentQuestion, state.panelIndex, state.questionIndex, autosaveInputs, autosaveSelects, enqueueSave, hasSelectInputs]);

  // Navigation logic
  const next = async (data) => {
    // If we are on last question -> enter review
    const qCount = Object.keys(currentPanel?.questions || {}).length;
    // Force-save immediately before navigation (bypass debounce)
    const forcedRevision = ++writeRevisionRef.current;
    const forcedSeq = ++clientWriteSeqRef.current;
    await saveQuestionNow(data, {}, forcedRevision, forcedSeq);
    if (inFlightRef.current) await inFlightRef.current;
    if (state.questionIndex >= qCount) {
      // Move to next panel or review if last panel
      const idx = panelKeys.indexOf(state.panelIndex);
      if (idx < panelKeys.length - 1) {
        setState(prev => ({ ...prev, panelIndex: panelKeys[idx + 1], questionIndex: 1 }));
      } else {
        setState(prev => ({ ...prev, mode: 'review' }));
      }
    } else {
      setState(prev => ({ ...prev, questionIndex: prev.questionIndex + 1 }));
    }
  };

  const prev = () => {
    if (state.mode === 'review') {
      // Go back to last question
      const lastPanel = panelKeys[panelKeys.length - 1];
      const lastQ = Object.keys(panels[lastPanel]?.questions || {}).length || 1;
      setState(prevState => ({ ...prevState, mode: 'questions', panelIndex: lastPanel, questionIndex: lastQ }));
      return;
    }
    if (state.questionIndex > 1) {
      setState(prev => ({ ...prev, questionIndex: prev.questionIndex - 1 }));
    } else {
      const idx = panelKeys.indexOf(state.panelIndex);
      if (idx > 0) {
        const prevPanel = panelKeys[idx - 1];
        const prevQ = Object.keys(panels[prevPanel]?.questions || {}).length || 1;
        setState(prev => ({ ...prev, panelIndex: prevPanel, questionIndex: prevQ }));
      }
    }
  };

  const skip = async (data) => {
    const forcedRevision = ++writeRevisionRef.current;
    const forcedSeq = ++clientWriteSeqRef.current;
    await saveQuestionNow(data, { isSkipping: true }, forcedRevision, forcedSeq);
    if (inFlightRef.current) await inFlightRef.current;
    await next(data);
  };

  const saveAndExit = async () => {
    try {
      // Force flush any pending autosave
      try { await autosaveInputs.flush(); } catch {}
      try { await autosaveSelects.flush(); } catch {}
      if (inFlightRef.current) await inFlightRef.current;
    } catch (_) {}
    navigate('/patient-dashboard');
  };

  // Prefill notice: show if provider-filled answers exist and user hasn't seen question yet
  const hasPrefillData = useMemo(() => {
    if (!currentQuestion?.subquestions) return false;
    return Object.values(currentQuestion.subquestions).some(sq =>
      sq.answer !== undefined && sq.answer !== null && sq.answer !== 'No answer given' && !currentQuestion.hasSeenQuestion
    );
  }, [currentQuestion]);

  // Minutes remaining estimate (kept for display); ~30s per question
  const estimate = useMemo(() => {
    const perQuestionSeconds = 30;
    const remaining = Math.max(totalQuestions - answeredCount, 0);
    const minutes = Math.round((remaining * perQuestionSeconds) / 60);
    return { minutes };
  }, [totalQuestions, answeredCount]);

  // Compute per-subsection (panel) progress for footer segments
  const panelProgress = useMemo(() => {
    return panelKeys.map((pKey) => {
      const panel = panels[pKey];
      if (!panel || !panel.questions) return 0;
      const questionEntries = Object.entries(panel.questions);
      if (questionEntries.length === 0) return 0;
      let answered = 0;
      questionEntries.forEach(([qIdx, q]) => {
        const wasSkipped = q?.wasSkipped === true;
        const hasAnswer = q?.subquestions && Object.values(q.subquestions).some(sq => (
          sq.answer !== null && sq.answer !== undefined && sq.answer !== 'No answer given'
        ));
        if (wasSkipped || hasAnswer) answered += 1;
      });
      return Math.max(0, Math.min(100, Math.round((answered / questionEntries.length) * 100)));
    });
  }, [panels, panelKeys]);

  // Render helpers
  const renderSubquestionInput = (subIdx, subq) => {
    const answerKey = `question_${state.panelIndex}_${state.questionIndex}_${subIdx}`;
    const currentValue = watch(answerKey);

    if (subq.inputType === 'freeTextArea') {
      return (
        <textarea
          className="w-full p-2 border rounded-md bg-white text-black min-h-[60px] max-h-[200px] resize-none overflow-y-auto"
          placeholder={subq.placeholder || ''}
          {...register(answerKey)}
          ref={createTextareaRefCallback(register(answerKey).ref)}
          onInput={(e) => resizeTextarea(e.target)}
          rows={2}
        />
      );
    }

    if (subq.inputType === 'freeTextNumerical') {
      return (
        <input
          type="text"
          className="w-full p-2 border rounded-md bg-white text-black"
          placeholder={subq.placeholder || ''}
          {...register(answerKey)}
          onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ''); }}
        />
      );
    }

    if (subq.inputType === 'date') {
      return (
        <DateOfBirthPicker
          value={currentValue || ''}
          onChange={(date) => setValue(answerKey, date)}
          placeholder={subq.placeholder || 'Select date'}
          className="w-full"
        />
      );
    }

    if (subq.inputType === 'selectOne' || subq.inputType === 'selectMultiple') {
      const isMulti = subq.inputType === 'selectMultiple';
      const valueArray = Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : [];
      const hasNoneOptions = subq.noneOptions && Array.isArray(subq.noneOptions) && subq.noneOptions.length > 0;

      const toggle = (option) => {
        if (isMulti) {
          let updated = valueArray;
          // selecting a normal option removes any none-options
          if (hasNoneOptions) {
            updated = updated.filter(v => !(subq.noneOptions || []).includes(v));
          }
          if (updated.includes(option)) {
            updated = updated.filter(v => v !== option);
          } else {
            updated = [...updated, option];
          }
          setValue(answerKey, updated);
        } else {
          setValue(answerKey, currentValue === option ? null : option);
        }
      };

      const selectNone = (noneOption) => {
        if (isMulti) {
          const isSelected = valueArray.includes(noneOption);
          setValue(answerKey, isSelected ? [] : [noneOption]);
        } else {
          setValue(answerKey, currentValue === noneOption ? null : noneOption);
        }
      };

      return (
        <div className="space-y-1.5">
          {(subq.options || []).map((opt) => {
            const isSelected = isMulti ? valueArray.includes(opt) : currentValue === opt;
            return (
              <div
                key={opt}
                onClick={() => toggle(opt)}
                className={`group p-3 rounded-xl border-2 cursor-pointer transition-all duration-300 ease-out flex items-center gap-3 ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              >
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-300 ease-out flex items-center justify-center ${isSelected ? 'bg-primary border-primary scale-105' : 'border-gray-300 bg-white group-hover:border-gray-400'}`}
                >
                  {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                </div>
                <div className={`font-medium transition-colors duration-300 ${isSelected ? 'text-primary' : 'text-gray-900'}`}>{opt}</div>
                {/* Show inline other input when applicable and selected */}
                {subq.otherOptions && opt in (subq.otherOptions) && isSelected && (
                  <textarea
                    className="font-medium w-full mt-2 p-2 border rounded-md bg-white text-black min-h-[60px] max-h-[200px] resize-none overflow-y-auto transition-all duration-300"
                    placeholder={subq.otherOptions[opt] || 'Please provide details'}
                    {...register(otherOptionFieldKey(answerKey, opt))}
                    ref={createTextareaRefCallback(
                      register(otherOptionFieldKey(answerKey, opt)).ref
                    )}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    onInput={(e) => resizeTextarea(e.target)}
                    rows={2}
                  />
                )}
              </div>
            );
          })}
          {isMulti && hasNoneOptions && (
            <>
              <div className="text-center text-gray-700 my-3 font-medium">or</div>
              {(subq.noneOptions || []).map(noneOpt => {
                const isSelected = valueArray.includes(noneOpt);
                return (
                  <div
                    key={noneOpt}
                    onClick={() => selectNone(noneOpt)}
                    className={`group p-3 rounded-xl border-2 cursor-pointer transition-all duration-300 ease-out flex items-center gap-3 ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                  >
                    <div
                      className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-300 ease-out flex items-center justify-center ${isSelected ? 'bg-primary border-primary scale-105' : 'border-gray-300 bg-white group-hover:border-gray-400'}`}
                    >
                      {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                    </div>
                    <div className={`font-medium transition-colors duration-300 ${isSelected ? 'text-primary' : 'text-gray-900'}`}>{noneOpt}</div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      );
    }

    // Default single-line text
    return (
      <input
        type={subq.inputType === 'freeText' ? 'text' : subq.inputType}
        className="w-full p-2 border rounded-md bg-white text-black"
        placeholder={subq.placeholder || ''}
        {...register(answerKey)}
      />
    );
  };

  // Early guard
  if (!currentFormData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="p-6 text-left">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900">{formTitle}</h1>
          <p className="mt-2 text-gray-600">Loading form…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      {/* Fixed header with Home and Saving indicator */}
      <div className="sticky top-0 left-0 right-0 bg-white z-30 relative" style={{ boxShadow: '0 24px 36px rgba(255,255,255,0.95), 0 12px 20px rgba(255,255,255,0.85)' }}>
        {/* Top row: right-aligned Saving + Home */}
        <div className="max-w-xl mx-auto px-10 pt-3 pb-1 flex items-center justify-end gap-3">
          <div className={`flex items-center text-sm text-gray-600 transition-opacity ${state.saving ? 'opacity-100' : 'opacity-0'}`} aria-live="polite" aria-atomic="true">
            <RefreshIcon className={`w-4 h-4 mr-2 ${state.saving ? 'animate-spin-slow' : ''} text-primary`} />
            Saving…
          </div>
          <button aria-label="Return home and save" onClick={saveAndExit} className="p-2 rounded-md hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <HomeIcon className="w-6 h-6 text-primary" />
          </button>
        </div>
        {/* Second row: formTitle and Part X/Y: subsection title */}
        <div className="max-w-xl mx-auto px-10 pb-3">
          <div className="text-left">
            <div className="text-2xl font-bold text-gray-900">{formTitle}</div>
            <div className="text-base mt-1 text-gray-900">
              <span className="font-bold">Part {(() => { const idx = panelKeys.indexOf(state.panelIndex); return (idx >= 0 ? idx + 1 : 1) + '/' + panelKeys.length; })()}: </span>
              <span className="font-normal">{currentPanel?.formSubsectionTitle || ''}</span>
            </div>
          </div>
        </div>
        {/* Soft white fade to blend header into content */}
        <div className="pointer-events-none absolute bottom-32 left-0 right-0 h-6">
          <div className="h-full w-full bg-gradient-to-b from-white via-white/80 to-transparent" />
        </div>
      </div>

      {/* Spacer under fixed header */}
      <div className="h-14" />

      <div className="flex-grow max-w-xl px-10 pb-24">
        <div className="text-left mb-4">
          <div ref={liveRegionRef} className="sr-only" aria-live="polite" aria-atomic="true"></div>

          {state.mode === 'questions' && currentQuestion && (
            <form onSubmit={handleSubmit(next)}>
              {/* Question title only - section and counters are shown in the fixed header */}
              <h3 className="font-bold mb-3">{currentQuestion.questionTitle}</h3>

              {hasPrefillData && (
                <div className="mb-3 bg-blue-50 border-blue-200 border rounded-2xl p-4 text-left">
                  <div className="text-sm font-semibold text-blue-700">Some information has been added by your provider.</div>
                </div>
              )}

              {/* Subquestions */}
              {Object.entries(currentQuestion.subquestions || {}).map(([subIdx, subq]) => (
                <div key={subIdx} className="mb-3">
                  <div className="flex flex-col gap-2">
                    <span className="mb-2 text-base md:text-base text-gray-800">{subq.subquestionDisplayText}</span>
                    <div className="w-full">
                      {renderSubquestionInput(subIdx, subq)}
                    </div>
                  </div>
                </div>
              ))}

              {/* Actions */}
              <div className="mt-6 grid grid-cols-2 gap-3 items-center">
                <div className="flex items-center gap-3">
                  {currentQuestion.hasSkipButton && (
                    <button type="button" onClick={handleSubmit(skip)} className="font-semibold py-2 px-4 rounded-md border border-gray-300 bg-white text-primary hover:bg-gray-50">{currentQuestion.skipButtonText || "I'm unable to provide this information"}</button>
                  )}
                  <button type="button" onClick={prev} className="font-semibold py-2 px-3 text-primary hover:bg-gray-50 inline-flex items-center"><ChevronLeftIcon className="w-5 h-5 mr-1" />Back</button>
                </div>
                <div className="flex justify-end">
                  <button type="submit" className="font-semibold py-2 px-3 rounded-lg bg-primary text-white hover:opacity-90 inline-flex items-center">
                    <span className="">Next</span>
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </form>
          )}

          {state.mode === 'review' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold">Review your answers</h2>
                <div className="text-sm text-gray-600">{answeredCount} of {totalQuestions} answered</div>
              </div>
              {submittedAnswers.length === 0 ? (
                <div className="text-gray-500">No answers yet.</div>
              ) : (
                submittedAnswers.map((entry, idx) => (
                  <div key={entry.questionVarName || idx} className="p-4 mb-4 rounded-lg bg-gray-50 border border-gray-200">
                    <h3 className="font-bold mb-2 text-gray-700">{entry.questionTitle}</h3>
                    {entry.answers.map((ans, i) => (
                      <div key={i} className="flex gap-4 text-gray-700">
                        <span className="w-72 font-semibold">{ans.question}</span>
                        <span className="flex-1">{formatDisplayAnswer(null, ans.answer)}</span>
                      </div>
                    ))}
                  </div>
                ))
              )}

              <div className="flex justify-between">
                <button type="button" onClick={prev} className="font-semibold py-2 px-4 rounded-md border border-gray-300 bg-white text-primary hover:bg-gray-50">Back</button>
                <button type="button" onClick={saveAndExit} className="font-semibold py-2 px-4 rounded-md bg-primary text-white hover:opacity-90">Complete • Save & Exit</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed footer: per-subsection progress segments */}
      <div className="fixed left-0 right-0 bottom-0 bg-white border-t border-gray-200 px-4 py-3">
        <div className="text-center text-xs text-gray-700 mb-2">About {estimate.minutes} minutes remaining</div>
        <div className="flex gap-2 max-w-xl mx-auto">
          {panelKeys.map((k, i) => (
            <div key={k} className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className={`bg-primary h-2 ${prefersReducedMotion ? '' : 'transition-all duration-300'}`} style={{ width: `${panelProgress[i]}%` }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


